import { parseDicomDateTime } from '../dicom/utils';

/**
 * Resolved PET metadata for SUV computation.
 * Time fields are pre-resolved to single date+time values
 * by DicomImageSlice — no fallback logic here.
 */
export interface PetSeriesMetadata {
    /** Patient weight in kilograms (0010,1030) */
    patientWeightKg?: number;
    /** Radionuclide total dose in Bq (0054,0016)[0].(0018,1074) */
    radionuclideTotalDose?: number;
    /** Radionuclide half life in seconds (0054,0016)[0].(0018,1075) */
    radionuclideHalfLife?: number;
    /** Resolved scan datetime as DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) */
    scanDateTime?: string;
    /** Resolved injection datetime as DICOM DateTime string (YYYYMMDDHHMMSS.FFFFFF) */
    injectionDateTime?: string;
}

export function computeSUVbwFactor(
    petMetadata: PetSeriesMetadata
): number | null {
    const {
        patientWeightKg,
        radionuclideTotalDose,
        radionuclideHalfLife,
        scanDateTime,
        injectionDateTime,
    } = petMetadata;

    if (!patientWeightKg || patientWeightKg <= 0) {
        return null;
    }
    if (!radionuclideTotalDose || radionuclideTotalDose <= 0) {
        return null;
    }
    if (!radionuclideHalfLife || radionuclideHalfLife <= 0) {
        return null;
    }
    if (!scanDateTime || !injectionDateTime) {
        return null;
    }

    const elapsedSeconds =
        parseDicomDateTime(scanDateTime) -
        parseDicomDateTime(injectionDateTime);

    if (elapsedSeconds < 0) {
        return null;
    }

    const decayedDose =
        radionuclideTotalDose *
        Math.pow(2, -elapsedSeconds / radionuclideHalfLife);

    if (decayedDose <= 0) {
        return null;
    }

    return (patientWeightKg * 1000) / decayedDose;
}

/**
 * Checks which DICOM parameters required for SUV computation are missing.
 */
export function getMissingSUVParams(petMetadata: PetSeriesMetadata): string[] {
    const missing: string[] = [];

    if (!petMetadata.radionuclideTotalDose) {
        missing.push('Radionuclide Total Dose (0018,1074)');
    }
    if (!petMetadata.radionuclideHalfLife) {
        missing.push('Radionuclide Half Life (0018,1075)');
    }
    if (!petMetadata.patientWeightKg) {
        missing.push('Patient Weight (0010,1030)');
    }
    if (!petMetadata.scanDateTime) {
        missing.push('Scan Date/Time (0008,0022+0008,0032)');
    }
    if (!petMetadata.injectionDateTime) {
        missing.push(
            'Radiopharmaceutical Start DateTime (0018,1078/0018,1072)'
        );
    }

    return missing;
}
